import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import CreatableSelect from 'react-select/creatable';
import { components as RSComponents } from 'react-select';
import { BombiixTagsInput, BombiixTagsInputProps } from '../../../../Components/BombiixTagsInput';
import { useTranslation } from 'react-i18next';
import { AddIcon, ChevronIcon, CloseIcon } from '../../../../bombiix-icon';
import type { BombiixCellRendererParams, BombiixRowId } from '../../../../Components/BombiixAGGrid';
import { TableRow } from './types';
import closeMenuOnAnchorScroll from '../../../../Components/BombiixAGGrid/cells/closeMenuOnAnchorScroll';

/** A tag suggestion (loaded from other products) shown in the dropdown. */
interface TagOption {
  label: string;
  value: string;
}

interface TagsCellExtras {
  /** True when this row is the active add/edit row (editable); false = read-only badges. */
  isInlineRow?: (row: TableRow) => boolean;
  /** Existing tags from other products, offered as dropdown suggestions. */
  suggestions?: TagOption[];
  /** Called when the inline cell first needs suggestions (mirrors BombiixTagsInput onFocus). */
  onLoadSuggestions?: () => void;
}

type TagsCellProps = BombiixCellRendererParams<TableRow, string> & TagsCellExtras;

/** How a chip row is fitted to one line: chips shown, the "+N" count, and the class marking a measuring pass. */
interface TagFit {
  shownCount: number;
  pillCount: number;
  rowClass: string;
}

/** The row stores tags as a comma-separated string (buildProductPayload splits on ","). */
const parseTags = (value: unknown): string[] =>
  typeof value === 'string' && value.trim() ? [...new Set(value.split(',').map((tag) => tag.trim()).filter(Boolean))] : [];

/* Matches the `gap` between chips, the "+" and the "+N" pill in ProductTableView.css. */
const TAG_GAP_PX = 4;

const TEXT_COLOR = 'var(--color-text)';

type SelectStyleBase = Record<string, unknown>;

interface SelectStyleState {
  isFocused?: boolean;
}

/* The master-design select spec (_select.scss / NewSelectMultiSelect) lives in react-select's
   emotion styles, not in CSS, so a bare CreatableSelect falls back to react-select's 38px
   control. Restate the 28px control here; the chip mirrors the Figma "Chip" component
   (Bombiix 2026 › node 10607:266932) — 11px radius, $color-green-bg fill, $color-green
   border, 12px #3c3c3c label. */
const tagSelectStyles = {
  menuPortal: (base: SelectStyleBase) => ({ ...base, zIndex: 9999 }),
  control: (base: SelectStyleBase, state: SelectStyleState) => ({
    ...base,
    minHeight: '28px',
    height: '28px',
    border: `1px solid ${state?.isFocused ? 'var(--color-blue)' : 'var(--color-grey-light)'}`,
    borderRadius: '4px',
    padding: '0',
    boxShadow: 'none',
    overflow: 'hidden',
    '&:hover': { borderColor: state?.isFocused ? 'var(--color-blue)' : 'var(--color-grey-light)' },
  }),
  valueContainer: (base: SelectStyleBase) => ({
    ...base,
    height: '100%',
    padding: '0 0 0 6px',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    gap: '4px',
  }),
  input: (base: SelectStyleBase) => ({ ...base, margin: 0, padding: 0, color: TEXT_COLOR, fontSize: '12px' }),
  placeholder: (base: SelectStyleBase) => ({
    ...base,
    color: 'var(--color-grey-dark)',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  indicatorsContainer: (base: SelectStyleBase) => ({ ...base, height: '100%', paddingRight: '12px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: SelectStyleBase) => ({
    ...base,
    padding: '0',
    color: 'var(--color-grey-dark)',
    fontSize: '16px',
    '&:hover': { color: TEXT_COLOR },
  }),
  menu: (base: SelectStyleBase) => ({
    ...base,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)',
    marginTop: '4px',
    overflow: 'hidden',
    border: 'none',
  }),
  menuList: (base: SelectStyleBase) => ({
    ...base,
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  }),
  option: (base: SelectStyleBase, state: SelectStyleState) => ({
    ...base,
    minHeight: '24px',
    height: 'auto',
    flexShrink: 0,
    padding: '4px 6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '4px',
    lineHeight: '16px',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    backgroundColor: state?.isFocused ? 'var(--pim-surface-selected)' : 'transparent',
    color: TEXT_COLOR,
    cursor: 'pointer',
    fontFamily: 'var(--font-atkiv-medium)',
    fontSize: '12px',
    '&:active': { backgroundColor: 'var(--pim-surface-selected)' },
  }),
  multiValue: (base: SelectStyleBase) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    margin: 0,
    padding: '2px 2px 2px 6px',
    borderRadius: '11px',
    backgroundColor: 'var(--color-green)',
    filter: 'drop-shadow(0px 0.25px 0.25px rgba(0, 0, 0, 0.25))',
    flexShrink: 0,
    minWidth: 0,
    maxWidth: '140px',
  }),
  multiValueLabel: (base: SelectStyleBase) => ({
    ...base,
    padding: 0,
    paddingLeft: 0,
    color: TEXT_COLOR,
    fontFamily: 'var(--font-atkiv-regular)',
    fontSize: '12px',
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  multiValueRemove: (base: SelectStyleBase) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '16px',
    height: '16px',
    padding: 0,
    borderRadius: '50%',
    color: TEXT_COLOR,
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.4)', color: TEXT_COLOR },
  }),
};

interface RSInnerProps {
  children?: React.ReactNode;
  [key: string]: unknown;
}

/* react-select v4 ships no bundled types here, so RSComponents members resolve to
   `unknown` — cast as NewSelectMultiSelect does. */
const RSMultiValueRemove = RSComponents.MultiValueRemove as React.ComponentType<RSInnerProps>;
const RSDropdownIndicator = RSComponents.DropdownIndicator as React.ComponentType<RSInnerProps>;

/** Chip close control — the Bombiix CloseIcon in place of react-select's default SVG. */
function BombiixMultiValueRemove(props: RSInnerProps) {
  return <RSMultiValueRemove {...props}>
    <CloseIcon size={16} aria-hidden="true" />
  </RSMultiValueRemove>
}

/** Chevron — Bombiix glyph, flipped 180° while the menu is open (mirrors NewSelectMultiSelect). */
function BombiixDropdownIndicator(props: RSInnerProps) {
  const isOpen = Boolean((props.selectProps as { menuIsOpen?: boolean } | undefined)?.menuIsOpen);
  return (
    <RSDropdownIndicator {...props}>
      <ChevronIcon
        size={20}
        aria-hidden="true"
        style={{ transform: isOpen ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s ease' }}
      />
    </RSDropdownIndicator>
  );
}

/**
 * Tags cell for the Product Information table view. Mirrors the ProductModal's tags input:
 * on the active add/edit row it renders a searchable Creatable multi-select — type a free
 * string and press Enter to add it as a badge, or pick an existing tag from the suggestions
 * dropdown; selected tags render as badges inline to the RIGHT of the input. Every other
 * (list) row shows read-only badges. The value stays a comma-separated string so the existing
 * payload/mapper handling is unchanged.
 */
function TagsCell(params: TagsCellProps) {
  const { value, data, colDef, onRowChange, getRowId, placeholder, isInlineRow, suggestions = [], onLoadSuggestions } = params;
  const { t } = useTranslation('product-information');
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const editable = Boolean(data && isInlineRow?.(data));
  const [showAllTags, setShowAllTags] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const cellRef = useRef<HTMLDivElement | null>(null);
  const [popupRect, setPopupRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const [local, setLocal] = useState<string[]>(parseTags(value));
  /* Empty cell opens straight into the picker (the add form's default state); once a tag is
     committed the cell collapses to chips + "+", which reopens it. */
  const [isAdding, setIsAdding] = useState(() => parseTags(value).length === 0);
  /* True only while the user is actively in the picker, so tags injected from OUTSIDE the cell
     (the Description "add as tags" checkbox) do not yank the input away mid-type. */
  const pickerFocusedRef = useRef(false);
  const menuOpenRef = useRef(false);
  const [, setMenuPlacementTick] = useState(0);

  useEffect(() => {
    const next = parseTags(value);
    setLocal(next);
    /* Tags can arrive from outside this cell — the Description "add as tags" checkbox pushes the
       description's words into the row's tags. When that happens the cell should collapse to
       badges + "+", not keep showing the empty "add tag" input. Only auto-collapse while the user
       is NOT actively typing here (pickerFocusedRef), so their own in-progress entry is untouched. */
    if (!pickerFocusedRef.current) setIsAdding(next.length === 0);
  }, [value]);

  const selected = useMemo<TagOption[]>(() => local.map((tag) => ({ label: tag, value: tag })), [local]);

  const commit = useCallback(
    (next: TagOption[]) => {
      const tags = [...new Set(next.map((option) => String(option.value).trim()).filter(Boolean))];
      setLocal(tags);
      if (rowId !== undefined && field) onRowChange?.(rowId, field, tags.join(', '));
    },
    [rowId, field, onRowChange],
  );

  /* AG Grid cells clip overflow, so the popup is portalled to <body> and positioned from the
     cell's rect — the same approach DescriptionCell uses for its suggestions menu. */
  useEffect(() => {
    if (!showAllTags || !cellRef.current) return undefined;
    const measure = () => {
      const rect = cellRef.current?.getBoundingClientRect();
      if (rect) setPopupRect({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [showAllTags]);

  /* Portalled popup sits outside the cell, so close on any click that is in neither. */
  useEffect(() => {
    if (!showAllTags) return undefined;
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!popupRef.current?.contains(target) && !cellRef.current?.contains(target)) setShowAllTags(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [showAllTags]);

  /* The same control the ProductModal's Tags card uses, so the popup matches that flow: type a
     tag (or several, comma-separated) and pick from the same suggestions. */
  const popupTagsProps: BombiixTagsInputProps = {
    tags: selected.map((option) => ({ id: option.value, name: option.label })),
    suggestions: suggestions.map((option) => ({ id: option.value, name: option.label })),
    allowNew: true,
    delimiters: [',', 'Enter', 'Tab'],
    placeholderText: t('TAGS.ADD_TAG_PLACEHOLDER'),
    onFocus: () => onLoadSuggestions?.(),
    onAddition: (tag: { name?: string }) => {
      const name = String(tag?.name ?? '').trim();
      if (name && !local.includes(name)) commit([...selected, { label: name, value: name }]);
    },
    onDelete: (index: number) => commit(selected.filter((_, position) => position !== index)),
  };

  const chipsRef = useRef<HTMLDivElement | null>(null);
  const chipRowRef = editable ? chipsRef : cellRef;
  const fit = useTagsThatFit(chipRowRef, local, !editable || !isAdding);
  const chipClass = (base: string, index: number) => `${base}${index >= fit.shownCount ? ' ptv-tag-badge--overflow' : ''}`;
  const toggleAllTags = () => {
    onLoadSuggestions?.();
    setShowAllTags((open) => !open);
  };
  const morePill = <MoreTagsPill count={fit.pillCount} label={t('TAGS.TITLE')} isOpen={showAllTags} onToggle={toggleAllTags} />;
  const allTagsPopup = (
    <AllTagsPopup rect={showAllTags ? popupRect : null} popupRef={popupRef} title={t('TAGS.TITLE')} closeLabel={t('TAGS.CLOSE')} onClose={() => setShowAllTags(false)}>
      {editable ? <BombiixTagsInput {...popupTagsProps} /> : <ReadonlyTagList tags={local} />}
    </AllTagsPopup>
  );

  if (!editable) {
    if (!local.length) return <div className="cag-cell" />;
    return (
      <div ref={cellRef} className={`cag-cell ptv-tags-cell ptv-tags-cell--readonly${fit.rowClass}`}>
        {local.map((tag, index) => (
          <span key={tag} data-tag-chip="" className={chipClass('ptv-tag-badge', index)} title={tag}>
            <span className="ptv-tag-badge__label">{tag}</span>
          </span>
        ))}
        {morePill}
        {allTagsPopup}
      </div>
    );
  }

  const removeTag = (tag: string) => commit(selected.filter((option) => option.value !== tag));

  /* react-select/creatable has no bundled TS types (v4) — it is declared as an untyped
     component (declarations.d.ts). Pass its props through an untyped bag so the strongly
     typed Creatable props (isMulti, value, onChange, portal props) still compile. */
  const selectProps: Record<string, unknown> = {
    isMulti: true,
    classNamePrefix: 'bombiix-select',
    className: 'ptv-tags-select',
    options: suggestions,
    value: selected,
    onChange: (next: unknown) => {
      const picked = (next as TagOption[] | null) ?? [];
      commit(picked);
      setIsAdding(picked.length === 0);
    },
    onBlur: () => {
      pickerFocusedRef.current = false;
      setIsAdding(local.length === 0);
    },
    onFocus: () => {
      pickerFocusedRef.current = true;
      onLoadSuggestions?.();
    },
    controlShouldRenderValue: false,
    /*
     * No clear-all (×) control on the input: tags are removed only from the badges themselves (each
     * chip's own × / the popup), so a bulk-clear cross on the picker is intentionally suppressed.
     */
    isClearable: false,
    placeholder: placeholder ?? colDef?.headerName ?? 'Add tag',
    formatCreateLabel: (input: string) => input,
    menuPosition: 'fixed',
    menuPlacement: 'auto',
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
    menuShouldBlockScroll: true,
    closeMenuOnScroll: closeMenuOnAnchorScroll({
      anchor: cellRef,
      isOpen: () => menuOpenRef.current,
      onFollow: () => setMenuPlacementTick((tick) => tick + 1),
    }),
    onMenuOpen: () => {
      menuOpenRef.current = true;
    },
    onMenuClose: () => {
      menuOpenRef.current = false;
    },
    styles: tagSelectStyles,
    components: {
      MultiValueRemove: BombiixMultiValueRemove,
      DropdownIndicator: BombiixDropdownIndicator,
    },
  };


  return (
    <div className="cag-cell-wrap">
      <div ref={cellRef} className="cag-cell ptv-tags-cell">
        {isAdding ? (
          <CreatableSelect {...selectProps} />
        ) : (
          <div ref={chipsRef} className={`ptv-tags-chips${fit.rowClass}`}>
            {local.map((tag, index) => (
              <span key={tag} data-tag-chip="" className={chipClass('ptv-tag-badge ptv-tag-badge--removable', index)} title={tag}>
                <span className="ptv-tag-badge__label">{tag}</span>
                <button
                  type="button"
                  className="ptv-tag-badge__remove"
                  aria-label={t('TAGS.REMOVE_TAG', { tag })}
                  onClick={() => removeTag(tag)}
                >
                  <CloseIcon size={16} aria-hidden="true" />
                </button>
              </span>
            ))}
            <button
              type="button"
              data-tag-fixed=""
              className="ptv-tags-add bombiix-icon-container bombiix-icon-container--green"
              aria-label={t('TAGS.ADD_TAG')}
              onClick={() => {
                onLoadSuggestions?.();
                setIsAdding(true);
              }}
            >
              <AddIcon size={16} aria-hidden="true" />
            </button>
            {morePill}
          </div>
        )}
        {allTagsPopup}
      </div>
    </div>
  );
}

export default TagsCell;

/**
 * How many tag chips fit on one line, leaving room for the fixed controls and, whenever some chips
 * are left out, for the "+N" pill. At least one chip always shows (it truncates); a row that has
 * not been laid out yet (no width) shows them all.
 * @param chipWidths natural width of each chip, in order
 * @param available width of the chip row
 * @param fixedWidth width of the controls always shown on the row (the "+" button), 0 when none
 * @param morePillWidth width of the "+N" pill
 * @returns the number of chips to show
 */
export function countTagsThatFit(chipWidths: number[], available: number, fixedWidth: number, morePillWidth: number): number {
  const total = chipWidths.length;
  if (available <= 0 || total === 0) return total;
  const widthOf = (count: number): number => {
    const hasMore = count < total;
    const items = count + (fixedWidth > 0 ? 1 : 0) + (hasMore ? 1 : 0);
    const chips = chipWidths.slice(0, count).reduce((sum, width) => sum + width, 0);
    return chips + fixedWidth + (hasMore ? morePillWidth : 0) + TAG_GAP_PX * Math.max(0, items - 1);
  };
  for (let count = total; count > 1; count -= 1) {
    if (widthOf(count) <= available) return count;
  }
  return 1;
}

/**
 * Fit a chip row to one line. Every chip is rendered and measured before paint whenever the tags
 * change (the hook returns null for that pass), and the count is recalculated from those widths
 * when the column resizes, so the row height never changes.
 * @param rowRef the chip row
 * @param tags the tags on the row
 * @param active whether the chip row is on screen
 * @returns how many chips to show, the "+N" count (0 when none is due) and the row's measuring class
 */
function useTagsThatFit(rowRef: MutableRefObject<HTMLDivElement | null>, tags: string[], active: boolean): TagFit {
  const [fitted, setFitted] = useState<number | null>(null);
  const measuredRef = useRef<{ chips: number[]; fixed: number; more: number }>({ chips: [], fixed: 0, more: 0 });

  useLayoutEffect(() => {
    setFitted(null);
  }, [tags, active, rowRef]);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!active || !row || fitted !== null) return;
    const widthsOf = (selector: string) => [...row.querySelectorAll<HTMLElement>(selector)].map((element) => element.offsetWidth);
    measuredRef.current = {
      chips: widthsOf('[data-tag-chip]'),
      fixed: widthsOf('[data-tag-fixed]').reduce((sum, width) => sum + width, 0),
      more: widthsOf('[data-tag-more]')[0] ?? 0,
    };
    const { chips, fixed, more } = measuredRef.current;
    setFitted(countTagsThatFit(chips, row.clientWidth, fixed, more));
  }, [active, fitted, rowRef, tags]);

  useEffect(() => {
    const row = rowRef.current;
    if (!active || !row || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const { chips, fixed, more } = measuredRef.current;
      if (chips.length) setFitted(countTagsThatFit(chips, row.clientWidth, fixed, more));
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [active, rowRef]);

  if (fitted === null) return { shownCount: tags.length, pillCount: tags.length, rowClass: ' ptv-tags-row--measuring' };
  return { shownCount: fitted, pillCount: tags.length - fitted, rowClass: '' };
}

interface MoreTagsPillProps {
  count: number;
  label: string;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * The "+N" pill standing in for the chips that do not fit on the row.
 * @param props the hidden-chip count, its accessible label and the popup toggle
 * @returns the pill, or nothing when every chip is shown
 */
function MoreTagsPill({ count, label, isOpen, onToggle }: MoreTagsPillProps) {
  if (count === 0) return null;
  return (
    <button
      type="button"
      data-tag-more=""
      className="ptv-tags-more bombiix-icon-container bombiix-icon-container--green"
      aria-label={label}
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      {`+${count}`}
    </button>
  );
}

interface AllTagsPopupProps {
  rect: { top: number; left: number; width: number } | null;
  popupRef: MutableRefObject<HTMLDivElement | null>;
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Every tag of a row in a panel portalled to <body>, since AG Grid cells clip overflow.
 * @param props where to anchor the panel, its labels, the close handler and the panel body
 * @returns the portalled panel, or nothing while it is closed (no anchor)
 */
function AllTagsPopup({ rect, popupRef, title, closeLabel, onClose, children }: AllTagsPopupProps) {
  if (!rect) return null;
  return createPortal(
    <div className="ptv-tags-popup" ref={popupRef} style={{ top: rect.top, left: rect.left, minWidth: rect.width }}>
      <div className="bombiix-modal__bar">
        <span className="bombiix-modal__spacer" />
        <span className="bombiix-modal__title">{title}</span>
        <button type="button" className="bombiix-modal__close" aria-label={closeLabel} onClick={onClose}>
          <CloseIcon size={16} aria-hidden="true" />
        </button>
      </div>
      <div className="bombiix-modal__body">{children}</div>
    </div>,
    document.body,
  );
}

/**
 * A row's tags as read-only chips, for the all-tags panel of a list row.
 * @param props the tags to show
 * @returns the chip list
 */
function ReadonlyTagList({ tags }: { tags: string[] }) {
  return (
    <div className="ptv-tags-popup__list">
      {tags.map((tag) => (
        <span key={tag} className="ptv-tag-badge" title={tag}>
          <span className="ptv-tag-badge__label">{tag}</span>
        </span>
      ))}
    </div>
  );
}
